'use client';

import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import { toast as sonnerToast } from 'sonner';
import { CheckCircleFillIcon, WarningIcon } from './icons';
import { cn } from '@/lib/utils';
import { useRTLUtils } from '@/lib/translation';

const iconsByType: Record<'success' | 'error', ReactNode> = {
  success: <CheckCircleFillIcon />,
  error: <WarningIcon />,
};

export function toast(props: Omit<ToastProps, 'id'>) {
  return sonnerToast.custom(
    (id) => (
      <Toast id={id} type={props.type} title={props.title} description={props.description} />
    ),
    { style: { zIndex: 400 } },
  );
}

function Toast(props: ToastProps) {
  const { id, type, title, description } = props;
  const { directionClass, textAlignClass } = useRTLUtils();

  const descriptionRef = useRef<HTMLDivElement>(null);
  const [multiLine, setMultiLine] = useState(false);

  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;

    const update = () => {
      const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight);
      const lines = Math.round(el.scrollHeight / lineHeight);
      setMultiLine(lines > 1);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => ro.disconnect();
  }, [description]);

  return (
    <div className={cn('flex w-full toast-mobile:w-[356px] justify-center', directionClass)}>
      <div
        data-testid="toast"
        key={id}
        className={cn(
          'bg-zinc-100 dark:bg-gray-900 p-3 rounded-lg w-full toast-mobile:w-fit flex flex-row gap-3 shadow-lg ring-1 ring-black/5 dark:ring-white/10',
          multiLine || title ? 'items-start' : 'items-center',
          directionClass,
        )}
      >
        <div
          data-type={type}
          className={cn(
            'data-[type=error]:text-red-600 data-[type=success]:text-green-600',
            { 'pt-0.5': multiLine || title },
          )}
        >
          {iconsByType[type]}
        </div>
        <div className={cn('flex flex-col gap-0.5', textAlignClass)}>
          {title && (
            <div className="text-zinc-950 dark:text-zinc-50 text-sm font-semibold">
              {title}
            </div>
          )}
          <div ref={descriptionRef} className="text-zinc-950 dark:text-zinc-50 text-sm">
            {description}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ToastProps {
  id: string | number;
  type: 'success' | 'error';
  /** Optional bold title shown above the description. */
  title?: string;
  description: string;
}
